// /app/frontend/src/pages/risk/dpia/steps/LawfulBasisStep.jsx
import React from 'react';
import { Label } from '../../../../components/ui/label';
import { Textarea } from '../../../../components/ui/textarea';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Separator } from '../../../../components/ui/separator';
import { Check } from 'lucide-react';
import { LAWFUL_BASIS_PERSONAL, LAWFUL_BASIS_SPECIAL } from '../constants';

export default function LawfulBasisStep({ formData, updateField }) {
  return (
    <div className="space-y-6">
      {/* Lawful Basis for Processing Personal Data */}
      <div className="space-y-4">
        <div>
          <h4 className="font-medium text-base">Lawful Basis for Processing Personal Data</h4>
          <p className="text-sm text-muted-foreground mt-1">Select all applicable lawful bases</p>
        </div>
        
        {/* Card-style selection grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {LAWFUL_BASIS_PERSONAL.map(basis => {
            const isSelected = formData.lawful_basis_personal.includes(basis.value);
            return (
              <div 
                key={basis.value}
                onClick={() => {
                  const current = formData.lawful_basis_personal;
                  const updated = isSelected 
                    ? current.filter(b => b !== basis.value)
                    : [...current, basis.value];
                  updateField('lawful_basis_personal', updated);
                }}
                className={`
                  relative p-4 rounded-lg border-2 cursor-pointer transition-all duration-200
                  ${isSelected 
                    ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/30 shadow-sm' 
                    : 'border-border hover:border-teal-300 hover:bg-muted/50'}
                `}
                data-testid={`lawful-basis-${basis.value}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`
                    w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5
                    ${isSelected ? 'border-teal-500 bg-teal-500' : 'border-muted-foreground/50'}
                  `}>
                    {isSelected && <Check className="h-3 w-3 text-white" />}
                  </div>
                  <div>
                    <p className={`font-medium text-sm ${isSelected ? 'text-teal-700 dark:text-teal-300' : ''}`}>
                      {basis.label}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">{basis.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Justification for Legitimate Interest - Only when legitimate interests selected */}
      {formData.lawful_basis_personal.includes('legitimate_interests') && (
        <div className="space-y-2 p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <Label htmlFor="legitimate_interest_justification" className="text-amber-800 dark:text-amber-300">
            Justification for Legitimate Interest <span className="text-red-500">*</span>
          </Label>
          <p className="text-xs text-amber-700 dark:text-amber-400 mb-2">
            A Legitimate Interests Assessment (LIA) must be conducted. Document your balancing test below.
          </p>
          <Textarea 
            id="legitimate_interest_justification"
            placeholder="1. Purpose of processing&#10;2. Necessity for legitimate interest&#10;3. Balancing test (your interests vs data subject's rights)&#10;4. Safeguards in place..."
            value={formData.legitimate_interest_justification}
            onChange={(e) => updateField('legitimate_interest_justification', e.target.value)}
            rows={5}
            className="resize-none bg-white dark:bg-background"
          />
        </div>
      )}

      {/* Consent Process - When consent is selected */}
      {formData.lawful_basis_personal.includes('consent') && (
        <div className="space-y-2 p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <Label htmlFor="consent_process_personal" className="text-blue-800 dark:text-blue-300">
            Consent Process Details
          </Label>
          <Textarea 
            id="consent_process_personal"
            placeholder="Describe how consent is obtained, recorded, and managed. Include details about withdrawal process..."
            value={formData.consent_process_personal}
            onChange={(e) => updateField('consent_process_personal', e.target.value)}
            rows={3}
            className="resize-none bg-white dark:bg-background"
          />
        </div>
      )}

      <Separator />

      {/* Sensitive Data Toggle */}
      <div className="space-y-4">
        <div className="flex items-center space-x-3 p-4 bg-muted/50 rounded-lg">
          <Checkbox 
            id="involves_sensitive_data"
            checked={formData.involves_sensitive_data}
            onCheckedChange={(checked) => {
              updateField('involves_sensitive_data', checked);
              if (!checked) {
                updateField('lawful_basis_special', []);
                updateField('consent_process_special', '');
              }
            }}
          />
          <div>
            <Label htmlFor="involves_sensitive_data" className="text-sm font-medium cursor-pointer">
              This processing involves special category (sensitive) data
            </Label>
            <p className="text-xs text-muted-foreground mt-0.5">
              Health, biometric, genetic, racial/ethnic origin, political opinions, religious beliefs, trade union membership, sex life/orientation
            </p>
          </div>
        </div>

        {/* Special Category Data Options - Only shown when sensitive data is selected */}
        {formData.involves_sensitive_data && (
          <div className="space-y-4 pl-4 border-l-4 border-teal-500">
            <div>
              <h4 className="font-medium text-base">Special Category Basis</h4>
              <p className="text-sm text-muted-foreground mt-1">Select the legal basis for processing sensitive data</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {LAWFUL_BASIS_SPECIAL.map(basis => {
                const isSelected = formData.lawful_basis_special.includes(basis.value);
                return (
                  <div 
                    key={basis.value}
                    onClick={() => {
                      const current = formData.lawful_basis_special;
                      const updated = isSelected 
                        ? current.filter(b => b !== basis.value)
                        : [...current, basis.value];
                      updateField('lawful_basis_special', updated);
                    }}
                    className={`
                      relative p-3 rounded-lg border cursor-pointer transition-all duration-200
                      ${isSelected 
                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/30' 
                        : 'border-border hover:border-teal-300 hover:bg-muted/50'}
                    `}
                    data-testid={`special-basis-${basis.value}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`
                        w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 mt-0.5
                        ${isSelected ? 'border-teal-500 bg-teal-500' : 'border-muted-foreground/50'}
                      `}>
                        {isSelected && <Check className="h-2.5 w-2.5 text-white" />}
                      </div>
                      <div>
                        <p className={`font-medium text-sm ${isSelected ? 'text-teal-700 dark:text-teal-300' : ''}`}>
                          {basis.label}
                        </p>
                        <p className="text-xs text-muted-foreground">{basis.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Explicit Consent Process - When explicit consent is selected */}
            {formData.lawful_basis_special.includes('explicit_consent') && (
              <div className="space-y-2 p-4 bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                <Label htmlFor="consent_process_special" className="text-purple-800 dark:text-purple-300">
                  Explicit Consent Process
                </Label>
                <p className="text-xs text-purple-700 dark:text-purple-400 mb-2">
                  Explicit consent requires a clear affirmative action. Describe how this is obtained.
                </p>
                <Textarea 
                  id="consent_process_special"
                  placeholder="Describe how explicit consent is obtained for special category data. Include the exact wording used, how consent is recorded, and the withdrawal process..."
                  value={formData.consent_process_special}
                  onChange={(e) => updateField('consent_process_special', e.target.value)}
                  rows={4}
                  className="resize-none bg-white dark:bg-background"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
